import { useCallback, useEffect, useRef, useState } from 'react';
import { FieldAnswer, Form, FormResponse } from '@app/api';
import { useGetFieldConfiguration } from './useGetFieldConfiguration';
import { getNextLogicPage } from './lib';

export const useFormState = ({
  form,
  onFinishForm,
}: {
  form?: Form;
  onFinishForm: (formResponse: FormResponse) => any;
}): [{ page: number; length: number }, (answer?: FieldAnswer) => any] => {
  const [answers, saveFieldAnswer] = useState<FieldAnswer[]>([]);
  const [page, setPage] = useState(-1);

  const questionsLength = !form ? 0 : form.fieldIds.length;
  const getFieldConfiguration = useGetFieldConfiguration(form, page);

  const next = useCallback(
    async (
      currentPage: number = page,
      fieldAnswer?: FieldAnswer,
    ): Promise<any> => {
      if (!form) return;

      const newAnswers = [...answers];

      if (fieldAnswer) {
        newAnswers.push(fieldAnswer);
        saveFieldAnswer(newAnswers);
      }

      const nextPage = getNextLogicPage(form, currentPage, fieldAnswer);
      if (nextPage === questionsLength) {
        return onFinishForm({ answers: newAnswers });
      }

      const { field, fieldConfiguration } = getFieldConfiguration(nextPage);
      if (!field || !fieldConfiguration) {
        return next(nextPage);
      }

      setPage(nextPage);
    },
    [
      setPage,
      page,
      questionsLength,
      form,
      getFieldConfiguration,
      onFinishForm,
      saveFieldAnswer,
      answers,
    ],
  );

  const sendAnswer = useCallback(
    async (fieldAnswer?: FieldAnswer) => next(undefined, fieldAnswer),
    [next],
  );

  const initNextCalled = useRef(false);
  useEffect(() => {
    if (page === -1 && !initNextCalled.current && !!form) {
      next();
      initNextCalled.current = true;
    }
  }, [next, page, form]);

  const length = (form && form.fields && form.fields.length) || 0;
  return [{ page, length }, sendAnswer];
};
